/**
 * Turning an Amplify Data response into a thrown error.
 *
 * The Amplify client does NOT reject when the API returns a GraphQL error. It resolves with
 * `{ data: null, errors: [...] }`. Code shaped like
 *
 *     const { data } = await client.queries.fetchSavedPlays();
 *     setPlays(data || []);
 *
 * therefore treats every server-side failure as an empty result, and a mutation written the same
 * way reports success for a write that never happened. That is exactly how the premium save/load
 * feature shipped broken and silent: "Load Play" sat permanently greyed out (empty list) while
 * saving showed "✓ Play saved!" (no throw), with the real reason only in the network tab.
 *
 * Call `throwOnErrors` on every Amplify Data response so failures become normal exceptions that
 * the surrounding try/catch already handles.
 */

/** The error shape Amplify returns; kept minimal so it fits every operation's response type. */
interface GraphQLErrorLike {
  message: string;
}

/**
 * Throws if the response carries GraphQL errors.
 *
 * The messages are joined rather than only the first, because the tier check and the ownership
 * check can both be reported for one request and seeing only one is misleading.
 */
export function throwOnErrors(errors?: readonly GraphQLErrorLike[] | null): void {
  if (errors?.length) {
    throw new Error(errors.map((e) => e.message).join('; '));
  }
}
